import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { useThemeColors, useStatusColor, getAlphaColor } from '../../theme/ColorUtils';
import authService, { User } from '../../services/authService';

// Define interface for API key
interface ApiKey {
  id: number | string;
  name: string;
  created_at: string;
  expires_at?: string;
  last_used: string | null;
  key?: string;
  masked_key?: string;
  usage_count?: number;
}

interface NewApiKey extends ApiKey {
  api_key?: string;
  message?: string;
}

const ApiKeyManager: React.FC = () => {
  const colors = useThemeColors();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const [newApiKey, setNewApiKey] = useState<NewApiKey | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [keyName, setKeyName] = useState<string>('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [keyToDelete, setKeyToDelete] = useState<ApiKey | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [openKeyRevealDialog, setOpenKeyRevealDialog] = useState<boolean>(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);

  useEffect(() => {
    fetchApiKeys();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    setUserLoading(true);
    try {
      const user = await authService.getUserProfile();
      setCurrentUser(user as User);
    } catch (err) {
      console.error('Error fetching current user:', err);
    } finally {
      setUserLoading(false);
    }
  };

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      // Check and log the current build mode for debugging
      const buildMode = process.env.REACT_APP_BUILD_MODE || 'unknown';
      // console.log('Current build mode from env:', buildMode);
      // console.log('Build time from env:', process.env.REACT_APP_BUILD_TIME);
      // console.log('API URL from env:', process.env.REACT_APP_API_URL);
      
      // Force production mode to ensure we're using DynamoDB
      const isDevMode = false; // Override to false to use real DynamoDB
      
      if (isDevMode) {
        console.log('Using mock API keys data for development mode (FORCED OVERRIDE)');
        // Mock API keys for development
        const mockApiKeys: ApiKey[] = [
          {
            id: 1,
            name: 'Production API Key',
            created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            expires_at: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
            last_used: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
          },
          {
            id: 2,
            name: 'Test API Key',
            created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
            expires_at: new Date(Date.now() + 80 * 24 * 60 * 60 * 1000).toISOString(),
            last_used: null
          }
        ];
        setApiKeys(mockApiKeys);
      } else {
        // Check if authenticated
        if (!authService.isAuthenticated()) {
          setError('Not authenticated. Please log in again.');
          setLoading(false);
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('Using real DynamoDB backend for API keys');
        }
        
        // In production, fetch from actual API with auth headers
        const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/v1/lis-portal/api-keys`;
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetching API keys from:', apiUrl);
        }
        
        const response = await axios.get(apiUrl, {
          headers: authService.getAuthHeaders()
        });
        
        // Ensure we're working with an array
        if (response.data) {
          if (process.env.NODE_ENV === 'development') {
            console.log('API keys fetched successfully');
          }
          
          if (Array.isArray(response.data)) {
            setApiKeys(response.data);
          } else if (response.data.api_keys && Array.isArray(response.data.api_keys)) {
            // Extract from api_keys property - this is our actual API response format
            if (process.env.NODE_ENV === 'development') {
              console.log('Found API keys in api_keys property, count:', response.data.api_keys?.length || 0);
            }
            setApiKeys(response.data.api_keys);
          } else if (response.data.data && Array.isArray(response.data.data)) {
            // Some APIs wrap arrays in a data property
            setApiKeys(response.data.data);
          } else if (response.data.keys && Array.isArray(response.data.keys)) {
            // Or they might be under a keys property
            setApiKeys(response.data.keys);
          } else if (response.data.items && Array.isArray(response.data.items)) {
            // Or they might be under an items property
            setApiKeys(response.data.items);
          } else if (response.data.results && Array.isArray(response.data.results)) {
            // Or they might be under a results property
            setApiKeys(response.data.results);
          } else if (typeof response.data === 'object') {
            // If it's an object with properties that look like API keys
            const parsedKeys = [];
            for (const key in response.data) {
              if (response.data[key] && typeof response.data[key] === 'object') {
                if (response.data[key].id && response.data[key].name) {
                  parsedKeys.push(response.data[key]);
                }
              }
            }
            if (parsedKeys.length > 0) {
              setApiKeys(parsedKeys);
            } else {
              // Last resort: initialize with empty array
              console.error('API response is not in expected format:', response.data);
              setApiKeys([]);
              setError('Received unexpected data format from API. Please try again later.');
            }
          } else {
            // If all else fails, set to empty array
            console.error('API response is not in expected format:', response.data);
            setApiKeys([]);
            setError('Received unexpected data format from API. Please try again later.');
          }
        } else {
          // No data, set empty array
          setApiKeys([]);
          setError('No API keys data received from server. Please try again later.');
        }
      }
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setApiKeys([]); // Ensure apiKeys is set to an array even on error
      setError('Failed to fetch API keys. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    try {
      // Force production mode to ensure we're using DynamoDB
      const isDevMode = false; // Override to false to use real DynamoDB
      
      if (isDevMode) {
        console.log('Using mock API key creation (FORCED OVERRIDE)');
        // Mock API key creation in development
        const newKey: NewApiKey = {
          id: Math.floor(Math.random() * 1000) + 3,
          name: keyName,
          created_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
          last_used: null,
          api_key: `lis_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
          message: 'API key created successfully'
        };
        setNewApiKey(newKey);
        setApiKeys([...apiKeys, newKey]);
      } else {
        // Check if authenticated
        if (!authService.isAuthenticated()) {
          showSnackbar('Not authenticated. Please log in again.', 'error');
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('Creating API key using real DynamoDB backend');
        }
        
        // In production, call actual API with auth headers
        const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/v1/lis-portal/api-keys`;
        if (process.env.NODE_ENV === 'development') {
          console.log('Creating API key at:', apiUrl);
          console.log('Authentication status:', authService.isAuthenticated());
          console.log('Key name being sent:', keyName);
        }
        
        const response = await axios.post(
          apiUrl, 
          { name: keyName },
          { headers: authService.getAuthHeaders() }
        );
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Create API key response received successfully');
          console.log('Response status:', response.status);
        }
        
        // Handle different response formats
        let newCreatedKey;
        if (response.data && response.data.success && response.data.api_key) {
          // Format: {success: true, api_key: {...}}
          console.log('Using api_key property from response');
          newCreatedKey = response.data.api_key;
        } else if (response.data && response.data.id) {
          // Direct key object
          console.log('Using direct key object from response');
          newCreatedKey = response.data;
        } else {
          // Unknown format, log error
          console.error('Unexpected response format from create API key endpoint:', response.data);
          throw new Error('Unexpected response format from API');
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('New created key object processed successfully');
        }
        setNewApiKey(newCreatedKey);
        setApiKeys([...apiKeys, newCreatedKey]);
      }
      setOpenDialog(false);
      setKeyName('');
      showSnackbar('API key created successfully', 'success');
    } catch (err) {
      console.error('Error creating API key:', err);
      if (axios.isAxiosError(err)) {
        console.error('Axios error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          headers: err.response?.headers
        });
      }
      showSnackbar('Failed to create API key', 'error');
    }
  };

  const handleDeleteApiKey = async () => {
    if (!keyToDelete) return;
    
    try {
      // Force production mode to ensure we're using DynamoDB
      const isDevMode = false; // Override to false to use real DynamoDB
      
      if (isDevMode) {
        console.log('Using mock API key deletion (FORCED OVERRIDE)');
        // Mock deletion in development
        setApiKeys(apiKeys.filter(key => key.id !== keyToDelete.id));
      } else {
        // Check if authenticated
        if (!authService.isAuthenticated()) {
          showSnackbar('Not authenticated. Please log in again.', 'error');
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('Deleting API key using real DynamoDB backend');
        }
        
        // In production, call actual API with auth headers
        const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/v1/lis-portal/api-keys/${keyToDelete.id}`;
        if (process.env.NODE_ENV === 'development') {
          console.log('Deleting API key at:', apiUrl);
        }
        
        const response = await axios.delete(
          apiUrl,
          { headers: authService.getAuthHeaders() }
        );
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Delete API key response received successfully');
        }
        
        // Check if the response indicates success
        if (response.data && response.data.success === false) {
          throw new Error(response.data.message || 'Failed to revoke API key');
        }
        
        // Update the UI by removing the deleted key
        setApiKeys(apiKeys.filter(key => key.id !== keyToDelete.id));
      }
      setOpenDeleteDialog(false);
      setKeyToDelete(null);
      showSnackbar('API key revoked successfully', 'success');
    } catch (err) {
      console.error('Error deleting API key:', err);
      let errorMessage = 'Failed to revoke API key';
      if (err instanceof Error) {
        errorMessage = err.message || errorMessage;
      }
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleCopyApiKey = (apiKey: string | undefined) => {
    if (!apiKey) {
      showSnackbar('No API key available to copy', 'error');
      return;
    }
    navigator.clipboard.writeText(apiKey);
    showSnackbar('API key copied to clipboard', 'success');
  };

  const handleShowKey = (key: ApiKey) => {
    setSelectedKey(key);
    setOpenKeyRevealDialog(true);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getDaysRemaining = (expiresAt: string | undefined) => {
    if (!expiresAt) return 365; // Default to 365 days if expires_at is not provided
    
    const now = new Date();
    const expiryDate = new Date(expiresAt);
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        if (text === currentUser?.id) {
          showSnackbar('Vendor ID copied to clipboard', 'success');
        } else {
          showSnackbar('API key copied to clipboard', 'success');
        }
      },
      () => {
        if (text === currentUser?.id) {
          showSnackbar('Failed to copy Vendor ID', 'error');
        } else {
          showSnackbar('Failed to copy API key', 'error');
        }
      }
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: colors.primary.main }}>
        API Key Management
      </Typography>
      
      <Typography variant="body1" paragraph>
        Manage your API keys for accessing the LIS API. Create new keys, view existing keys, and revoke keys as needed.
      </Typography>

      {/* Vendor ID Display */}
      {currentUser && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: getAlphaColor(colors.primary.main, 0.1), border: `1px solid ${colors.primary.main}` }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', color: colors.primary.main }}>
            <KeyIcon sx={{ mr: 1 }} />
            Vendor Credentials
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ mr: 2, fontWeight: 'medium' }}>
              Vendor ID:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              bgcolor: 'background.paper', 
              p: 1, 
              borderRadius: 1,
              border: `1px solid ${colors.border}`
            }}>
              <Typography variant="body1" sx={{ mr: 1, fontFamily: 'monospace' }}>
                {currentUser.id}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => copyToClipboard(currentUser.id)}
                title="Copy Vendor ID"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            This is your unique vendor identifier from Cognito. It's required for API authentication and webhook configuration.
          </Typography>
        </Paper>
      )}
      
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ 
            bgcolor: colors.primary.main,
            '&:hover': { bgcolor: colors.primary.dark }
          }}
        >
          Create New API Key
        </Button>
      </Box>
      
      {/* API Keys Table */}
      <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: colors.background.alt }}>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>API Key</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                    <Box sx={{ m: 1, position: 'relative' }}>
                      <CircularProgress size={40} />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                      Loading API keys...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Alert severity="error" sx={{ justifyContent: 'center' }}>
                    {error}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : apiKeys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    No API keys found. Create your first API key by clicking the button above.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              apiKeys.map((key) => {
                const daysRemaining = getDaysRemaining(key.expires_at);
                return (
                  <TableRow key={key.id}>
                    <TableCell>{key.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {key.masked_key || '••••••••••••••••'}
                        </Typography>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleShowKey(key)}
                          title="Show and copy API key"
                          sx={{ ml: 1 }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(key.created_at)}</TableCell>
                    <TableCell>
                      {formatDate(key.expires_at)}
                      <Box component="div" sx={{ mt: 0.5 }}>
                        <Chip 
                          label={`${daysRemaining} days remaining`} 
                          size="small"
                          color={daysRemaining < 7 ? 'error' : daysRemaining < 30 ? 'warning' : 'success'}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {key.last_used ? formatDate(key.last_used) : 'Never used'}
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="error" 
                        onClick={() => {
                          setKeyToDelete(key);
                          setOpenDeleteDialog(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Information Card */}
      <Card sx={{ 
        mt: 4, 
        p: 2, 
        bgcolor: getAlphaColor(colors.info, 0.1),
        border: `1px solid ${colors.border}`
      }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: colors.primary.main }}>
            About API Keys
          </Typography>
          <Typography variant="body2" paragraph>
            API keys are used to authenticate your requests to the LIS API. Keep your API keys secure and do not share them.
          </Typography>
          <Typography variant="body2" paragraph>
            Keys are valid for 365 days from creation. You'll need to create new keys before the old ones expire.
          </Typography>
          <Typography variant="body2" paragraph>
            If you suspect a key has been compromised, revoke it immediately and create a new one.
          </Typography>
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Generate New API Key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will generate a new API key for your LIS integration. The key will be valid for 365 days.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="API Key Name"
            fullWidth
            variant="outlined"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="e.g., Production Key, Test Key"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateApiKey} 
            variant="contained" 
            color="primary"
            disabled={!keyName.trim()}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete API Key Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke the API key "{keyToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteApiKey} variant="contained" color="error">
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      {/* New API Key Reveal Dialog */}
      <Dialog open={openKeyRevealDialog} onClose={() => setOpenKeyRevealDialog(false)}>
        <DialogTitle>API Key Details</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is your API key. Make sure to copy it and store it securely.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: colors.background.alt, borderRadius: 1 }}>
            <Typography variant="h6" component="div" sx={{ mb: 1 }}>
              {selectedKey?.name}
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: getAlphaColor(colors.warning, 0.1), 
              borderRadius: 1, 
              border: `1px solid ${colors.warning}`, 
              fontFamily: 'monospace',
              wordBreak: 'break-all'
            }}>
              {selectedKey?.key || 'Key not available'}
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CopyIcon />}
              onClick={() => handleCopyApiKey(selectedKey?.key)}
              sx={{ mt: 2 }}
              fullWidth
            >
              Copy API Key
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenKeyRevealDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Show successful API key creation dialog */}
      <Dialog open={Boolean(newApiKey)} onClose={() => setNewApiKey(null)}>
        <DialogTitle>API Key Generated</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your new API key has been generated. Please copy it now as you won't be able to see it again.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: colors.background.alt, borderRadius: 1 }}>
            <Typography variant="h6" component="div" sx={{ mb: 1 }}>
              {newApiKey?.name}
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: getAlphaColor(colors.warning, 0.1), 
              borderRadius: 1, 
              border: `1px solid ${colors.warning}`, 
              fontFamily: 'monospace',
              wordBreak: 'break-all'
            }}>
              {newApiKey?.key || newApiKey?.api_key || 'Key not available'}
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CopyIcon />}
              onClick={() => handleCopyApiKey(newApiKey?.key || newApiKey?.api_key)}
              sx={{ mt: 2 }}
              fullWidth
            >
              Copy API Key
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewApiKey(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApiKeyManager; 